export const KNOWLEDGE_BASE_CONTENTS_COLLAPSED_KEY = "attorneyhub.knowledgeBase.contentsCollapsed.v1";

type StorageLike = Pick<Storage, "getItem" | "removeItem" | "setItem">;

/**
 * Whether the reader has folded the contents panel away.
 *
 * Kept outside the page rather than in its state because every article has its own route, so
 * choosing something from the contents remounts the page. Held in component state, the panel
 * sprang open again the moment it was used, which is the one moment it is meant to stay shut.
 *
 * Absence means open: a reader who has never touched the toggle meets the panel as it always was.
 */
export function loadContentsCollapsed(storage = getBrowserLocalStorage()): boolean {
  if (!storage) return false;

  try {
    return storage.getItem(KNOWLEDGE_BASE_CONTENTS_COLLAPSED_KEY) === "true";
  } catch {
    return false;
  }
}

/**
 * Remembers the choice, and forgets it again when the panel is reopened so the key only exists
 * for readers who actually want it collapsed.
 */
export function saveContentsCollapsed(collapsed: boolean, storage = getBrowserLocalStorage()): void {
  if (!storage) return;

  try {
    if (collapsed) {
      storage.setItem(KNOWLEDGE_BASE_CONTENTS_COLLAPSED_KEY, "true");
      return;
    }

    storage.removeItem(KNOWLEDGE_BASE_CONTENTS_COLLAPSED_KEY);
  } catch (error) {
    // Storage the browser refuses — a private window with it disabled, a quota already full. The
    // panel still collapses; it simply does not remember across a reload. Warn rather than rethrow:
    // there is nothing the reader can do about it, but this is the only trace of why a choice they
    // made did not survive the next article.
    console.warn("Could not remember the knowledge base contents panel preference.", error);
  }
}

/**
 * Router state an article link in the contents carries, so the page it opens knows the article
 * was chosen from there.
 *
 * Where the contents stack above the article, choosing one leaves the panel open over it and the
 * article loads out of sight below, so the reader cannot tell anything happened. The page folds
 * the panel for that visit. It travels on the navigation rather than in the page's state because
 * the first choice made from the knowledge base's front page is a different route and remounts
 * the page; and it is not saved as the preference, because a fold the reader did not ask for
 * should not follow them to a wide screen.
 */
export const chosenFromContentsState = { chosenFromContents: true } as const;

export function wasChosenFromContents(locationState: unknown): boolean {
  return (
    typeof locationState === "object" &&
    locationState !== null &&
    (locationState as { chosenFromContents?: unknown }).chosenFromContents === true
  );
}

function getBrowserLocalStorage(): StorageLike | undefined {
  try {
    return globalThis.localStorage;
  } catch {
    return undefined;
  }
}
